import {apiSessionScope, captureSession, isApiSessionBound, sessionChangedError, invalidateApiAccess} from './sessionScope';
import {randomId} from './randomId';

export const OPERATIONS_CHANGED = 'autotu:operations-changed';
const PREFIX = 'autotu:operations:v1:';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const STATES = new Set(['unknown','pending','recoverable','succeeded','rejected']);
const unresolved = row => ['unknown','pending','recoverable'].includes(row.state);
const memory = new Map();
const snapshots = new Map(); // Runtime only. Never serialize JSON payloads into browser storage.
let queue = Promise.resolve();
export const operationHeader = (headers, name) => headers?.get?.(name) ?? Object.entries(headers || {}).find(([key])=>key.toLowerCase()===name.toLowerCase())?.[1];
const owner = (api, user) => JSON.stringify([api,user?.tenant_id,user?.id]);
const storageKey = row => PREFIX + JSON.stringify([row.api,row.tenant_id,row.user_id,row.operation_id]);
const emit = () => window.dispatchEvent(new Event(OPERATIONS_CHANGED));

export function operationError(state, row) {
  const messages = {
    succeeded:'Questa operazione è già registrata. Aggiorna la schermata senza inviarla di nuovo.',
    pending:'La richiesta è in corso. Verifica l’esito prima di inviarne un’altra.',
    recoverable:'La richiesta è stata ricevuta e deve completarsi. Usa “Completa la stessa richiesta”.',
    unknown:'L’esito non è ancora verificato. Usa “Verifica esito” prima di inviare un’altra richiesta.',
    blocked:'Esiste una richiesta per questa operazione da verificare. Controlla prima il suo esito.',
    storage:'Impossibile conservare il riferimento della richiesta su questo browser. Nessun invio eseguito.',
    protocol:'Impossibile verificare la ricevuta della richiesta. Controlla l’esito prima di riprovare.',
    changed:'I dati non coincidono con la richiesta da verificare. Ricostruisci gli stessi dati e le stesse versioni; nessun nuovo invio è stato eseguito.',
    reconstruct:'La copia della richiesta non è più in memoria. Riapri il modulo e ricostruisci gli stessi dati: il controllo dell’impronta impedirà invii diversi.',
  };
  const error = new Error(messages[state] || messages.unknown);
  error.code = 'ERR_OPERATION_' + state.toUpperCase();
  error.operationState = state;
  error.operationId = row?.operation_id;
  return error;
}

function validRow(row) {
  return row?.version===1 && UUID.test(row.operation_id || '') && STATES.has(row.state) &&
    typeof row.api==='string' && typeof row.tenant_id==='string' && typeof row.user_id==='string' &&
    typeof row.route==='string' && row.route.startsWith('/') && typeof row.method==='string';
}

export function listOperations(api, user, {includeSettled=false}={}) {
  if(!user)return [];
  const rows = new Map(), unreadable = [];
  for(let index=0;index<localStorage.length;index++) {
    const key=localStorage.key(index);
    if(!key?.startsWith(PREFIX))continue;
    // Ignore other accounts before parsing their records, including damaged
    // records. They cannot block or reveal data to the current account.
    let keyOwner;
    try {keyOwner=JSON.parse(key.slice(PREFIX.length));} catch {continue;}
    if(!Array.isArray(keyOwner)||JSON.stringify(keyOwner.slice(0,3))!==owner(api,user))continue;
    // A damaged record of this account cannot be verified or resumed: drop it
    // instead of blocking every later mutation.
    let row=null;
    try {row=JSON.parse(localStorage.getItem(key));} catch {/* dropped below */}
    if(validRow(row))rows.set(key,row);else unreadable.push(key);
  }
  for(const key of unreadable){try{localStorage.removeItem(key);}catch{/* storage unavailable: skipped anyway */}}
  for(const [key,row] of memory) {
    // A removed browser record is not resurrected from a previous login/test.
    if(localStorage.getItem(key)!=null)rows.set(key,row);
  }
  return [...rows.values()].filter(row=>owner(row.api,{tenant_id:row.tenant_id,id:row.user_id})===owner(api,user) &&
    (includeSettled || !row.acknowledged)).sort((a,b)=>a.created_at.localeCompare(b.created_at));
}

function storeRow(row, required=false) {
  const key=storageKey(row), value=JSON.stringify(row);
  try {
    localStorage.setItem(key,value);
    if(localStorage.getItem(key)!==value)throw new Error('Storage verification failed');
    memory.delete(key);
  } catch {
    if(required)throw operationError('storage');
    memory.set(key,{...row,storage_warning:true});
  }
  emit();
  return row;
}

function readRow(meta) {
  if(!meta)return null;
  const key=storageKey(meta);
  const raw=localStorage.getItem(key);
  if(raw==null)return null;
  const row=memory.get(key) || JSON.parse(raw);
  return validRow(row)?row:null;
}

function patchRow(meta, patch) {
  const row=readRow(meta);
  if(!row)return null;
  return storeRow({...row,...patch,updated_at:new Date().toISOString()});
}

export function markOperationUnknown(config) {
  if(!config?.reliableOperation)return;
  try {patchRow(config.reliableOperation,{state:'unknown',acknowledged:false});} catch {/* The original persisted reference remains recoverable. */}
}

function canonical(value) {
  if(Array.isArray(value))return '['+value.map(canonical).join(',')+']';
  if(value && typeof value==='object')return '{'+Object.keys(value).sort().map(key=>JSON.stringify(key)+':'+canonical(value[key])).join(',')+'}';
  return JSON.stringify(value);
}

async function hashBytes(bytes) {
  if(!globalThis.crypto?.subtle)throw operationError('storage');
  const hash=await globalThis.crypto.subtle.digest('SHA-256',bytes);
  return [...new Uint8Array(hash)].map(byte=>byte.toString(16).padStart(2,'0')).join('');
}
const digest=value=>hashBytes(new TextEncoder().encode(canonical(value)));
async function fileManifest(file) {
  const bytes=typeof file.arrayBuffer==='function'?await file.arrayBuffer():await new Promise((resolve,reject)=>{
    const reader=new FileReader();reader.onload=()=>resolve(reader.result);reader.onerror=()=>reject(operationError('protocol'));reader.readAsArrayBuffer(file);
  });
  return {name:file.name,type:file.type,size:file.size,sha256:await hashBytes(bytes)};
}
async function multipartManifest(data) {
  const entries=[...data.entries()];
  if(entries.length!==1 || entries[0][0]!=='file' || typeof entries[0][1]==='string')throw operationError('protocol');
  return {file:await fileManifest(entries[0][1])};
}

export function isReliableMutation(config, user) {
  if(!user || !['post','put','patch','delete'].includes(String(config.method).toLowerCase()))return false;
  const path=String(config.url || '').split('?')[0];
  if(/\/(?:preview|extension-price)$/.test(path))return false;
  if(typeof FormData!=='undefined' && config.data instanceof FormData)return path==='/upload'&&String(config.method).toLowerCase()==='post';
  return /^\/(?:vehicles|assignments|clients|rentals|quotes|vehicle-tariffs|tariffs|users|locations|maintenances|deadlines|costs|refuelings|damages|fines|suppliers|report-filters|customer-signals|site-pricing|site-documents)(?:\/|$)|^\/(?:settings\/company|notifications\/send-digest)$/.test(path);
}

// Axios evaluates runWhen synchronously, before its asynchronous interceptor
// chain. Capture the caller's session now, not after a possible account switch.
export function captureReliableMutation(config) {
  if(!isReliableMutation(config,apiSessionScope().user))return false;
  config.reliableSession=captureSession();
  return true;
}

async function locked(name, action) {
  const run=async()=>{
    if(!navigator.locks?.request)throw operationError('storage');
    return navigator.locks.request(name,action);
  };
  const pending=queue.then(run,run);
  queue=pending.catch(()=>{});
  return pending;
}

// This interceptor runs before Axios transforms the JSON body. It never resends.
export async function prepareReliableMutation(config, api) {
  const scope=apiSessionScope(), user=scope.user;
  if(!isReliableMutation(config,user))return config;
  const token=localStorage.getItem('autotu_token'), session=config.reliableSession || captureSession();
  session.assertCurrent();
  if(!isApiSessionBound(token))throw sessionChangedError();
  const path=String(config.url).split('?')[0], method=String(config.method).toUpperCase();
  const multipart=typeof FormData!=='undefined'&&config.data instanceof FormData;
  const uploadData=multipart?new FormData():null;
  if(multipart)for(const [name,value] of config.data.entries())uploadData.append(name,value);
  let body;
  try {body=multipart?await multipartManifest(uploadData):config.data==null?null:JSON.parse(typeof config.data==='string'?config.data:JSON.stringify(config.data));}
  catch {throw operationError('protocol');}
  const params=config.params==null?null:JSON.parse(JSON.stringify(config.params));
  const aliasNames=['request_id','delivery_request_id','return_request_id'];
  const aliases=aliasNames.map(key=>body?.[key]).filter(Boolean);
  const provided=operationHeader(config.headers,'Idempotency-Key');
  if([provided,...aliases].filter(Boolean).some(key=>!UUID.test(key)))throw operationError('protocol');
  let key=provided || aliases[0] || randomId();
  const site=operationHeader(config.headers,'X-Active-Site') ?? user.active_site_id ?? null;
  const ifMatch=operationHeader(config.headers,'If-Match') ?? null;
  return locked('autotu:operations:'+owner(api,user),async()=>{
    session.assertCurrent();
    const rows=listOperations(api,user,{includeSettled:true});
    const existing=rows.find(row=>row.operation_id===key);
    if(existing?.state==='succeeded')throw operationError('succeeded',existing);
    // A terminal rollback permits a new, explicit intention. Keep the domain
    // request_id untouched; only the transport identity is renewed.
    if(existing?.state==='rejected')key=randomId();
    const pending=existing&&unresolved(existing)?existing:rows.find(row=>unresolved(row)&&row.route===path&&row.site_id===site);
    if(pending) {
      if(!pending.not_found || pending.site_id!==site || pending.route!==path || pending.method!==method)throw operationError('blocked',pending);
      key=pending.operation_id;
      // Restore only opaque domain identifiers, never business values. This
      // lets a rebuilt form prove its exact digest after a browser reload.
      for(const [name,value] of Object.entries(pending.domain_keys || {})) {
        if(!body || !aliasNames.includes(name))throw operationError('changed',pending);
        body[name]=value;
      }
    }
    const descriptor={method,url:config.url,params,body,site,if_match:ifMatch};
    const hash=await digest(descriptor);
    session.assertCurrent();
    if(pending&&hash!==pending.payload_hash)throw operationError('changed',pending);
    const now=new Date().toISOString();
    const record={version:1,api,tenant_id:user.tenant_id,user_id:user.id,operation_id:key,
      method,route:path,site_id:site,kind:multipart?'upload':'json',payload_hash:hash,state:'pending',created_at:now,updated_at:now,
      domain_keys:Object.fromEntries(aliasNames.filter(name=>body?.[name]).map(name=>[name,body[name]])),
      acknowledged:false,resume_available:false,retry_allowed:false,not_found:false,
      ...(pending?{created_at:pending.created_at}:{})};
    storeRow(record,true);
    const frozen=(pending?snapshots.get(storageKey(record)):null) || {
      method,url:config.url,params:params==null?undefined:JSON.parse(JSON.stringify(params)),
      data:multipart?uploadData:body==null?undefined:JSON.stringify(body),
      headers:{...(ifMatch!=null?{'If-Match':ifMatch}:{}),...(!multipart&&body!=null?{'Content-Type':'application/json'}:{})},
      ...(config.handoverConfirmation?{handoverConfirmation:{...config.handoverConfirmation}}:{}),
    };
    snapshots.set(storageKey(record),frozen);
    config.reliableOperation=record;
    config.headers ??= {};
    config.headers['Idempotency-Key']=key;
    if(site!=null)config.headers['X-Active-Site']=site;
    config.data=frozen.data;
    if(!multipart&&body!==null)config.headers['Content-Type']='application/json';
    config.params=frozen.params;
    return config;
  });
}

export function acceptOperationResponse(response) {
  const meta=response?.config?.reliableOperation;
  if(!meta)return response;
  const state=operationHeader(response.headers,'X-Operation-Status');
  const id=operationHeader(response.headers,'X-Operation-Id');
  if(id!==meta.operation_id || !STATES.has(state) || response.status===202&&state==='succeeded') {
    markOperationUnknown(response.config);throw operationError('protocol',meta);
  }
  const replay=String(operationHeader(response.headers,'X-Operation-Replayed'))==='1';
  const successful=state==='succeeded'&&response.status!==202;
  patchRow(meta,{state:successful?'succeeded':state,acknowledged:successful&&!replay});
  if(successful)snapshots.delete(storageKey(meta));
  if(successful&&!replay)return response;
  if(replay&&successful)throw operationError('succeeded',meta);
  throw operationError(['pending','recoverable'].includes(state)?state:'protocol',meta);
}

export function rejectOperationResponse(error) {
  const meta=error?.config?.reliableOperation;
  if(!meta)return Promise.reject(error);
  const state=operationHeader(error.response?.headers,'X-Operation-Status');
  const id=operationHeader(error.response?.headers,'X-Operation-Id');
  if(id===meta.operation_id && state==='rejected') {
    patchRow(meta,{state:'rejected',acknowledged:true});
    snapshots.delete(storageKey(meta));
    return Promise.reject(error);
  }
  const next=id===meta.operation_id && ['pending','recoverable'].includes(state)?state:'unknown';
  patchRow(meta,{state:next,acknowledged:false});
  return Promise.reject(operationError(next,meta));
}

const RECEIPT_FIELDS=new Set(['operation_id','state','created_at','updated_at','retry_allowed','resume_available']);
export function acceptOperationReceipt(record, receipt) {
  if(!receipt || Object.keys(receipt).some(key=>!RECEIPT_FIELDS.has(key)) || receipt.operation_id!==record.operation_id ||
    !['succeeded','pending','recoverable','rejected'].includes(receipt.state) ||
    ['retry_allowed','resume_available'].some(key=>receipt[key]!=null && typeof receipt[key]!=='boolean'))throw operationError('protocol',record);
  if(['succeeded','rejected'].includes(receipt.state))snapshots.delete(storageKey(record));
  return patchRow(record,{state:receipt.state,resume_available:receipt.resume_available===true,retry_allowed:receipt.retry_allowed===true,not_found:false,acknowledged:false});
}

export function hasOperationSnapshot(record) {return snapshots.has(storageKey(record));}
export function clearOperationSnapshots() {snapshots.clear();}
// Logout: forget this account's operation records; other accounts keep theirs.
export function clearUserOperations(api, user) {
  const mine=key=>{try{const scope=JSON.parse(key.slice(PREFIX.length));return Array.isArray(scope)&&JSON.stringify(scope.slice(0,3))===owner(api,user);}catch{return false;}};
  const keys=[];
  for(let index=0;index<localStorage.length;index++){const key=localStorage.key(index);if(key?.startsWith(PREFIX)&&mine(key))keys.push(key);}
  for(const key of keys)localStorage.removeItem(key);
  for(const key of [...memory.keys()])if(mine(key))memory.delete(key);
  for(const key of [...snapshots.keys()])if(mine(key))snapshots.delete(key);
  emit();
}

export async function recoverOperation(apiClient, record, api, user, resume=false) {
  const session=captureSession();
  const live=apiSessionScope().user;
  if(!user || owner(api,user)!==owner(record.api,{tenant_id:record.tenant_id,id:record.user_id}) ||
    owner(api,live)!==owner(api,user))throw sessionChangedError();
  const current=readRow(record);
  if(!current)throw operationError('protocol',record);
  if(resume && ((!current.resume_available&&!current.not_found) || current.site_id!==(live.active_site_id ?? null)))throw operationError('blocked',record);
  const path=`/operations/${encodeURIComponent(record.operation_id)}`;
  try {
    if(resume&&current.not_found) {
      const snapshot=snapshots.get(storageKey(record));
      if(!snapshot)throw operationError('reconstruct',record);
      const response=await apiClient.request({...snapshot,params:snapshot.params?JSON.parse(JSON.stringify(snapshot.params)):undefined,
        headers:{...snapshot.headers,'Idempotency-Key':record.operation_id,'X-Active-Site':current.site_id}});
      session.assertCurrent();
      const completed=patchRow(record,{state:'succeeded',acknowledged:false,not_found:false});
      // The wizard may no longer be mounted during explicit recovery. Preserve
      // its draft for review, but remove a driver's stale access immediately.
      if(snapshot.handoverConfirmation&&response?.data?.access_refresh===true)invalidateApiAccess();
      return completed;
    }
    const response=resume?await apiClient.post(path+'/resume',{}):await apiClient.get(path);
    session.assertCurrent();
    return acceptOperationReceipt(record,response.data);
  } catch(error) {
    session.assertCurrent();
    if(resume&&error.operationState==='succeeded')return readRow(record);
    const detail=error.response?.data?.detail;
    if(!resume&&error.response?.status===404&&(detail==='OPERATION_NOT_FOUND'||detail?.code==='OPERATION_NOT_FOUND'))return patchRow(record,{state:'unknown',not_found:true,acknowledged:false});
    if(error.response?.data?.operation_id===record.operation_id)return acceptOperationReceipt(record,error.response.data);
    throw error;
  }
}

// Explicit file selection can recover an already stored upload. Linking it to
// the current form remains the caller's separate operation, never a banner side effect.
export async function uploadWithRecovery(apiClient, api, file, config={}) {
  const form=new FormData();form.append('file',file);
  const user=apiSessionScope().user;
  const session=captureSession(), site=user.active_site_id??null;
  const body={file:await fileManifest(file)};
  const hash=await digest({method:'POST',url:'/upload',params:null,body,site,if_match:null});
  session.assertCurrent();
  const prior=listOperations(api,user,{includeSettled:true}).find(row=>row.kind==='upload'&&row.site_id===site&&row.payload_hash===hash&&row.state!=='rejected');
  const result=async row=>{
    const response=await apiClient.get(`/uploads/${encodeURIComponent(row.operation_id)}/result`);
    session.assertCurrent();
    const data=response.data;
    if(!data || Object.keys(data).some(key=>!['id','path','filename','content_type'].includes(key)) ||
      !['id','path','filename','content_type'].every(key=>typeof data[key]==='string'&&data[key]))throw operationError('protocol',row);
    patchRow(row,{state:'succeeded',acknowledged:true});
    return response;
  };
  if(prior) {
    const current=await recoverOperation(apiClient,prior,api,user);
    if(current.state==='succeeded')return result(current);
    if(!current.not_found)throw operationError(current.state,current);
  }
  try{return await apiClient.post('/upload',form,{...config,headers:{...config.headers,...(prior?{'Idempotency-Key':prior.operation_id}:{})}});}
  catch(error){
    if(error.operationState==='succeeded'&&error.operationId) {
      const row=listOperations(api,user,{includeSettled:true}).find(item=>item.operation_id===error.operationId);
      if(row)return result(row);
    }
    throw error;
  }
}

export function acknowledgeOperation(record, api, user) {
  if(owner(api,user)!==owner(record.api,{tenant_id:record.tenant_id,id:record.user_id}))throw sessionChangedError();
  const current=readRow(record);
  if(!current || unresolved(current))throw operationError('blocked',record);
  patchRow(record,{acknowledged:true});
}
